import { useRef, useState } from 'react';
import { mapEvent, useEventsStore } from '@/hooks/useEventsStore';
import type { AdminReservation, Event } from '@/mocks/admin';
import StatusBadge from '@/components/base/StatusBadge';
import AdminEventDetailPanel from './AdminEventDetailPanel';
import { apiRequest } from '@/lib/api';
import { EVENT_CATEGORIES } from '@/constants/eventCategories';

const formatPrice = (n: number) => n === 0 ? 'Gratuit' : `${n.toLocaleString('fr-FR')} FCFA`;
const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
const ADMIN_PROFIT_FEE = 500;
const DEXPAY_PAYIN_RATE = 0.02;
const DEXPAY_PAYOUT_RATE = 0.02;
type Step = 1 | 2 | 3;
type TicketTypeForm = {
  id?: string;
  name: string;
  price: string;
  seats: string;
  description: string;
};

const getApprovalPriceBreakdown = (price: number) => {
  if (price <= 0) {
    return { base: 0, admin: 0, payin: 0, payout: 0, total: 0 };
  }

  const subtotal = price + ADMIN_PROFIT_FEE;
  const payin = Math.ceil(subtotal * DEXPAY_PAYIN_RATE);
  const payout = Math.ceil(price * DEXPAY_PAYOUT_RATE);

  return {
    base: price,
    admin: ADMIN_PROFIT_FEE,
    payin,
    payout,
    total: Math.ceil(subtotal + payin + payout),
  };
};

const getEventPriceBreakdown = (event: Event) => {
  if (event.originalTicketPrice !== undefined) {
    return {
      base: event.originalTicketPrice,
      admin: event.adminProfitFee ?? 0,
      payin: event.dexpayPayinFee ?? 0,
      payout: event.dexpayPayoutFee ?? 0,
      total: event.ticketPrice,
    };
  }

  return getApprovalPriceBreakdown(event.ticketPrice);
};

const normalizedTicketTypes = (ticketTypes: TicketTypeForm[]): NonNullable<Event['ticketTypes']> => (
  ticketTypes
    .filter((ticketType) => ticketType.name.trim())
    .map((ticketType) => ({
      id: ticketType.id,
      name: ticketType.name.trim(),
      description: ticketType.description.trim(),
      price: Number(ticketType.price || 0),
      seats: ticketType.seats === '' ? null : Number(ticketType.seats),
    }))
);

const ticketSummary = (ticketTypes: TicketTypeForm[]) => {
  const normalized = normalizedTicketTypes(ticketTypes);
  const prices = normalized.map((ticketType) => ticketType.price);
  const seats = normalized.reduce((sum, ticketType) => sum + Number(ticketType.seats ?? 0), 0);

  return {
    ticketTypes: normalized,
    ticketPrice: prices.length ? Math.min(...prices) : 0,
    totalSeats: seats,
  };
};

export default function PendingEvents() {
  const { events, addEvent, approveEvent, rejectEvent, updateEvent, deleteEvent, restoreEvent } = useEventsStore();
  const [rejectTarget, setRejectTarget] = useState<Event | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'deleted'>('pending');
  const [detailTarget, setDetailTarget] = useState<Event | null>(null);
  const [detailReservations, setDetailReservations] = useState<AdminReservation[]>([]);
  const [editTarget, setEditTarget] = useState<Event | null>(null);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [organizerFilter, setOrganizerFilter] = useState('Tous');
  const [categoryFilter, setCategoryFilter] = useState('Tous');
  const [priceFilter, setPriceFilter] = useState<'Tous' | 'Gratuits' | 'Payants'>('Tous');
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<Step>(1);
  const [createErrors, setCreateErrors] = useState<Partial<Record<string, string>>>({});
  const [createSaving, setCreateSaving] = useState(false);
  const [eventImageFile, setEventImageFile] = useState<File | null>(null);
  const [eventImagePreview, setEventImagePreview] = useState('');
  const eventImageInputRef = useRef<HTMLInputElement>(null);
  const editImageInputRef = useRef<HTMLInputElement>(null);
  const [createForm, setCreateForm] = useState({
    title: '',
    type: '',
    date: '',
    endDate: '',
    location: '',
    diocese: '',
    description: '',
    ticketPrice: '0',
    totalSeats: '',
    ticketTypes: [{ name: 'Standard', price: '0', seats: '', description: '' }] as TicketTypeForm[],
    organizerName: 'Administration',
    contact: '',
    externalLink: '',
  });
  const [editForm, setEditForm] = useState({
    title: '',
    type: '',
    date: '',
    endDate: '',
    ticketPrice: '',
    totalSeats: '',
    location: '',
    image: '',
    contact: '',
    description: '',
  });

  const organizerOptions = ['Tous', ...Array.from(new Set(events.map((event) => event.organizerName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'fr'))];
  const categoryOptions = [
    'Tous',
    ...Array.from(new Set([
      ...EVENT_CATEGORIES,
      ...events.map((event) => event.type).filter(Boolean),
    ])).sort((a, b) => a.localeCompare(b, 'fr')),
  ];
  const filtered = events.filter((e) => {
    const query = searchTerm.trim().toLowerCase();
    const matchesStatus = filter === 'all' || e.status === filter;
    const matchesOrganizer = organizerFilter === 'Tous' || e.organizerName === organizerFilter;
    const matchesCategory = categoryFilter === 'Tous' || e.type === categoryFilter;
    const matchesPrice = priceFilter === 'Tous'
      || (priceFilter === 'Gratuits' ? Number(e.ticketPrice) <= 0 : Number(e.ticketPrice) > 0);
    const matchesSearch =
      !query ||
      e.title.toLowerCase().includes(query) ||
      e.type.toLowerCase().includes(query) ||
      e.organizerName.toLowerCase().includes(query) ||
      e.location.toLowerCase().includes(query);

    return matchesStatus && matchesOrganizer && matchesCategory && matchesPrice && matchesSearch;
  });

  const approve = (id: string) => {
    approveEvent(id);
  };

  const reject = () => {
    if (!rejectTarget) return;
    rejectEvent(rejectTarget.id, rejectReason);
    setRejectTarget(null);
    setRejectReason('');
  };

  const openEdit = (event: Event) => {
    setEditTarget(event);
    setEditImageFile(null);
    setEditImagePreview(event.image);
    setEditForm({
      title: event.title,
      type: event.type,
      date: event.date,
      endDate: event.endDate,
      ticketPrice: String(event.ticketPrice),
      totalSeats: String(event.totalSeats),
      location: event.location,
      image: event.image,
      contact: event.contact,
      description: event.description,
    });
  };

  const handleEditImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setEditImageFile(file);
    setEditImagePreview(preview);
    setEditForm((current) => ({ ...current, image: preview }));
  };

  const saveEdit = async () => {
    if (!editTarget) return;

    setEditSaving(true);
    setEditError('');

    try {
      await updateEvent(editTarget.id, {
        ...editTarget,
        title: editForm.title,
        type: editForm.type,
        date: editForm.date,
        endDate: editForm.endDate || editForm.date,
        ticketPrice: Number(editForm.ticketPrice || 0),
        totalSeats: Number(editForm.totalSeats || 0),
        location: editForm.location,
        image: editImagePreview || editForm.image,
        imageFile: editImageFile,
        contact: editForm.contact,
        description: editForm.description,
      });

      setEditTarget(null);
      setEditImageFile(null);
      setEditImagePreview('');
    } catch (error) {
      setEditError(error instanceof Error ? error.message : "Impossible de modifier l'événement.");
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteEvent(deleteTarget.id);
    setDeleteTarget(null);
    if (detailTarget?.id === deleteTarget.id) {
      setDetailTarget(null);
    }
  };

  const restore = async (event: Event) => {
    await restoreEvent(event.id);
  };

  const setCreateField = (key: keyof typeof createForm, value: string) => {
    setCreateForm((current) => ({ ...current, [key]: value }));
  };

  const setCreateTicketTypeField = (index: number, field: keyof TicketTypeForm, value: string) => {
    setCreateForm((current) => ({
      ...current,
      ticketTypes: current.ticketTypes.map((ticketType, itemIndex) => (
        itemIndex === index ? { ...ticketType, [field]: value } : ticketType
      )),
    }));
  };

  const addCreateTicketType = () => {
    setCreateForm((current) => ({
      ...current,
      ticketTypes: [...current.ticketTypes, { name: '', price: '0', seats: '', description: '' }],
    }));
  };

  const removeCreateTicketType = (index: number) => {
    setCreateForm((current) => ({
      ...current,
      ticketTypes: current.ticketTypes.length > 1
        ? current.ticketTypes.filter((_, itemIndex) => itemIndex !== index)
        : current.ticketTypes,
    }));
  };

  const handleEventImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setEventImageFile(file);
    setEventImagePreview(preview);
  };

  const validateCreateStep1 = () => {
    const errors: Partial<Record<string, string>> = {};
    if (createForm.title.length < 3) errors.title = 'Minimum 3 caractères';
    if (!createForm.type) errors.type = 'Requis';
    if (!createForm.date) errors.date = 'Requis';
    else if (new Date(createForm.date) <= new Date()) errors.date = 'La date doit être dans le futur';
    if (!createForm.location) errors.location = 'Requis';
    if (!createForm.diocese.trim()) errors.diocese = 'Requis';
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCreateStep2 = () => {
    const errors: Partial<Record<string, string>> = {};
    const ticketTypes = normalizedTicketTypes(createForm.ticketTypes);

    if (ticketTypes.length === 0) {
      errors.ticketTypes = 'Ajoutez au moins un tarif';
    }
    if (createForm.ticketTypes.some((ticketType) => Number(ticketType.price) < 0)) {
      errors.ticketTypes = 'Prix ≥ 0';
    }
    if (createForm.ticketTypes.some((ticketType) => Number(ticketType.price) > 0 && (!ticketType.seats || Number(ticketType.seats) < 1))) {
      errors.ticketTypes = 'Chaque tarif payant doit avoir au moins 1 place';
    }

    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateNext = () => {
    if (createStep === 1 && validateCreateStep1()) setCreateStep(2);
    else if (createStep === 2 && validateCreateStep2()) setCreateStep(3);
  };

  const saveCreate = async () => {
    setCreateSaving(true);
    setCreateErrors({});

    try {
      const summary = ticketSummary(createForm.ticketTypes);

      await addEvent({
        title: createForm.title,
        type: createForm.type,
        date: createForm.date,
        endDate: createForm.endDate || createForm.date,
        location: createForm.location,
        diocese: createForm.diocese,
        description: createForm.description,
        ticketPrice: summary.ticketPrice,
        totalSeats: summary.ticketTypes.some((ticketType) => ticketType.price > 0) ? summary.totalSeats : 0,
        ticketTypes: summary.ticketTypes,
        soldSeats: 0,
        status: 'pending',
        organizerId: '',
        organizerName: createForm.organizerName || 'Administration',
        image: eventImagePreview,
        imageFile: eventImageFile,
        createdAt: new Date().toISOString(),
        contact: createForm.contact,
      });

      setCreateOpen(false);
      setCreateStep(1);
      setCreateErrors({});
      setEventImageFile(null);
      setEventImagePreview('');
      setCreateForm({
        title: '',
        type: '',
        date: '',
        endDate: '',
        location: '',
        diocese: '',
        description: '',
        ticketPrice: '0',
        totalSeats: '',
        ticketTypes: [{ name: 'Standard', price: '0', seats: '', description: '' }],
        organizerName: 'Administration',
        contact: '',
        externalLink: '',
      });
    } catch (error) {
      setCreateErrors({
        submit: error instanceof Error ? error.message : "Impossible de créer l'événement.",
      });
    } finally {
      setCreateSaving(false);
    }
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateStep(1);
  };

  const filterButtons: { key: typeof filter; label: string }[] = [
    { key: 'all', label: 'Tous' }, { key: 'pending', label: 'En attente' },
    { key: 'approved', label: 'Approuvés' }, { key: 'rejected', label: 'Rejetés' },
    { key: 'deleted', label: 'Corbeille' },
  ];

  const openDetails = async (event: Event) => {
    setDetailTarget(event);

    try {
      const freshEvent = await apiRequest<Parameters<typeof mapEvent>[0]>(`/admin/evenements/${event.id}`);
      setDetailTarget(mapEvent(freshEvent));

      const reservations = await apiRequest<Array<{
        id: string;
        event_id: string;
        buyer: string;
        email?: string | null;
        phone?: string | null;
        tickets: number;
        total: number;
        status: AdminReservation['status'];
        date: string;
        ticket_code: string;
      }>>(`/admin/evenements/${event.id}/reservations`);

      setDetailReservations(reservations.map((reservation) => ({
        id: reservation.id,
        eventId: reservation.event_id,
        buyer: reservation.buyer,
        email: reservation.email ?? '',
        phone: reservation.phone ?? '',
        tickets: reservation.tickets,
        total: Number(reservation.total ?? 0),
        status: reservation.status,
        date: reservation.date,
        ticketCode: reservation.ticket_code,
      })));
    } catch (error) {
      console.error('Admin event reservations error:', error);
      setDetailReservations([]);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-100">
            <i className="ri-calendar-event-line text-violet-600 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Gestion des Événements</h3>
            <p className="text-xs text-gray-400">
              {events.filter((e) => e.status === 'pending').length} en attente · {events.filter((e) => e.status === 'deleted').length} dans la corbeille
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {filterButtons.map((btn) => (
            <button key={btn.key} onClick={() => setFilter(btn.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filter === btn.key ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {btn.key === 'deleted' ? <i className="ri-delete-bin-line mr-1"></i> : null}
              {btn.label}
            </button>
          ))}
        </div>
        <button onClick={() => setCreateOpen(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gold text-white text-sm rounded-xl hover:bg-gold-dark cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>Ajouter
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"></i>
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Rechercher un événement..."
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
        </div>
        <select
          value={organizerFilter}
          onChange={(event) => setOrganizerFilter(event.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
        >
          {organizerOptions.map((organizer) => (
            <option key={organizer} value={organizer}>{organizer === 'Tous' ? 'Tous les organisateurs' : organizer}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
        >
          {categoryOptions.map((category) => (
            <option key={category} value={category}>{category === 'Tous' ? 'Toutes les catégories' : category}</option>
          ))}
        </select>
        <select
          value={priceFilter}
          onChange={(event) => setPriceFilter(event.target.value as typeof priceFilter)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
        >
          <option value="Tous">Tous les prix</option>
          <option value="Gratuits">Événements gratuits</option>
          <option value="Payants">Événements payants</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50">
              {['Événement', 'Organisateur', 'Date', 'Ticket', 'Places', 'Statut', 'Actions'].map((h) => (
                <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ev) => (
              <tr
                key={ev.id}
                onClick={() => void openDetails(ev)}
                className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer group"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={ev.image} alt={ev.title} className="w-12 h-9 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px] group-hover:text-navy transition-colors">{ev.title}</p>
                      <p className="text-xs text-gray-400">{ev.type}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{ev.organizerName}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{formatDate(ev.date)}</td>
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-gray-900">{formatPrice(ev.ticketPrice)}</p>
                  {ev.status === 'pending' && ev.ticketPrice > 0 && (
                    <p className="text-[11px] font-medium text-emerald-600">
                      Approuvé: {formatPrice(getEventPriceBreakdown(ev).total)}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs">
                    <span className="font-semibold text-gray-900">{ev.soldSeats}</span>
                    <span className="text-gray-400">/{ev.totalSeats}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={ev.status} /></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <button onClick={() => void openDetails(ev)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"><i className="ri-eye-line text-sm"></i></button>
                    {ev.status === 'deleted' ? (
                      <button onClick={() => void restore(ev)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 cursor-pointer" title="Restaurer"><i className="ri-arrow-go-back-line text-sm"></i></button>
                    ) : (
                      <>
                        <button onClick={() => openEdit(ev)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gold-100 text-gold-dark cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                        <button onClick={() => setDeleteTarget(ev)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-500 cursor-pointer" title="Mettre dans la corbeille"><i className="ri-delete-bin-line text-sm"></i></button>
                      </>
                    )}
                    {ev.status === 'pending' && (
                      <>
                        <button onClick={() => approve(ev.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-500 cursor-pointer"><i className="ri-check-line text-sm"></i></button>
                        <button onClick={() => { setRejectTarget(ev); setRejectReason(''); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-500 cursor-pointer"><i className="ri-close-line text-sm"></i></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-400"><i className="ri-inbox-line text-3xl block mb-2"></i><p className="text-sm">Aucun événement dans cette catégorie</p></div>
        )}
      </div>

      {rejectTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRejectTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl"><i className="ri-close-circle-line text-red-600 text-xl"></i></div>
              <div><h4 className="text-sm font-bold text-gray-900">Rejeter l'événement</h4><p className="text-xs text-gray-400 truncate max-w-[260px]">{rejectTarget.title}</p></div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Raison du rejet</label>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Expliquez pourquoi cet événement est rejeté..." className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-red-400" maxLength={500} />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRejectTarget(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={reject} disabled={!rejectReason.trim()} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold disabled:opacity-50 hover:bg-red-600 cursor-pointer whitespace-nowrap">Rejeter</button>
            </div>
          </div>
        </div>
      )}

      {detailTarget && (
        <AdminEventDetailPanel
          event={detailTarget}
          reservations={detailReservations}
          onClose={() => {
            setDetailTarget(null);
            setDetailReservations([]);
          }}
        />
      )}

      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-gold-100 rounded-lg">
                  <i className="ri-calendar-event-line text-gold-dark text-sm"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Ajouter un événement</h3>
                  <p className="text-xs text-gray-400">Même formulaire que dans l’espace organisateur</p>
                </div>
              </div>
              <button onClick={closeCreateModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0 overflow-x-auto">
              {([
                { step: 1, title: 'Infos événement' },
                { step: 2, title: 'Billetterie' },
                { step: 3, title: 'Aperçu' },
              ] as const).map((item) => (
                <div key={item.step} className="flex items-center gap-3 min-w-fit">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${createStep >= item.step ? 'bg-gold text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {item.step}
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${createStep >= item.step ? 'text-navy' : 'text-gray-400'}`}>{item.title}</p>
                  </div>
                  {item.step < 3 && <div className="w-10 h-px bg-gray-200"></div>}
                </div>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              <div className="max-w-2xl space-y-4">
                {createStep === 1 && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Titre de l'événement *</label>
                        <input value={createForm.title} onChange={e => setCreateField('title', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Concert Gospel de Dakar" />
                        {createErrors.title && <p className="text-xs text-red-500 mt-1">{createErrors.title}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie *</label>
                        <select value={createForm.type} onChange={e => setCreateField('type', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                          <option value="">Choisir une catégorie</option>
                          {EVENT_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                        </select>
                        {createErrors.type && <p className="text-xs text-red-500 mt-1">{createErrors.type}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Diocèse / zone *</label>
                        <input value={createForm.diocese} onChange={e => setCreateField('diocese', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Dakar" />
                        {createErrors.diocese && <p className="text-xs text-red-500 mt-1">{createErrors.diocese}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Date & heure de début *</label>
                        <input type="datetime-local" value={createForm.date} onChange={e => setCreateField('date', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                        {createErrors.date && <p className="text-xs text-red-500 mt-1">{createErrors.date}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Date & heure de fin</label>
                        <input type="datetime-local" value={createForm.endDate} onChange={e => setCreateField('endDate', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Lieu *</label>
                        <input value={createForm.location} onChange={e => setCreateField('location', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Cathédrale de Dakar" />
                        {createErrors.location && <p className="text-xs text-red-500 mt-1">{createErrors.location}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Description *</label>
                        <textarea value={createForm.description} onChange={e => setCreateField('description', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-28" placeholder="Décrivez votre événement, le programme, les intervenants..." maxLength={700} />
                        <div className="flex justify-between items-center mt-1">
                          {createErrors.description ? <p className="text-xs text-red-500">{createErrors.description}</p> : <span />}
                          <p className="text-xs text-gray-400">{createForm.description.length}/700</p>
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Image / affiche de l'événement</label>
                        <div className="flex items-center gap-4">
                          <div className="w-24 h-20 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0">
                            {eventImagePreview ? (
                              <img src={eventImagePreview} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <i className="ri-image-line text-gray-300 text-2xl"></i>
                            )}
                          </div>
                          <div className="space-y-2">
                            <input ref={eventImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleEventImageChange} />
                            <button type="button" onClick={() => eventImageInputRef.current?.click()} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                              Choisir une image
                            </button>
                            <p className="text-xs text-gray-400">Une affiche claire améliore la validation de l'événement.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {createStep === 2 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-600">Types de tickets *</p>
                          <p className="text-xs text-gray-400">Ex: Standard, VIP, Couple, Enfant</p>
                        </div>
                        <button type="button" onClick={addCreateTicketType} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-navy hover:bg-gray-50">
                          <i className="ri-add-line"></i> Ajouter un tarif
                        </button>
                      </div>
                      {createForm.ticketTypes.map((ticketType, index) => (
                        <div key={index} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.4fr_1fr_1fr_auto]">
                            <input value={ticketType.name} onChange={(event) => setCreateTicketTypeField(index, 'name', event.target.value)} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Nom du tarif" />
                            <input type="number" value={ticketType.price} onChange={(event) => setCreateTicketTypeField(index, 'price', event.target.value)} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Prix FCFA" />
                            <input type="number" value={ticketType.seats} onChange={(event) => setCreateTicketTypeField(index, 'seats', event.target.value)} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Places" />
                            <button type="button" onClick={() => removeCreateTicketType(index)} disabled={createForm.ticketTypes.length === 1} className="flex h-10 w-10 items-center justify-center rounded-xl text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30">
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                          <input value={ticketType.description} onChange={(event) => setCreateTicketTypeField(index, 'description', event.target.value)} className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Description courte optionnelle" />
                        </div>
                      ))}
                      {createErrors.ticketTypes && <p className="text-xs text-red-500">{createErrors.ticketTypes}</p>}
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                        Prix affiché : {formatPrice(ticketSummary(createForm.ticketTypes).ticketPrice)} · Places totales : {ticketSummary(createForm.ticketTypes).totalSeats || 'illimitées / non renseignées'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Nom de l'organisateur</label>
                      <input value={createForm.organizerName} onChange={e => setCreateField('organizerName', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Foi Events Senegal" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Contact</label>
                      <input value={createForm.contact} onChange={e => setCreateField('contact', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: +221 77 000 00 00" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Lien externe (optionnel)</label>
                      <input value={createForm.externalLink} onChange={e => setCreateField('externalLink', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: https://..." />
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-2 bg-amber-50 rounded-xl px-4 py-3">
                      <i className="ri-information-line text-amber-600"></i>
                      <p className="text-xs text-amber-700">Ajoutez autant de catégories que nécessaire. Si un tarif est payant, renseignez le nombre de places.</p>
                    </div>
                  </div>
                )}

                {createStep === 3 && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-28 h-24 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {eventImagePreview ? <img src={eventImagePreview} alt="" className="w-full h-full object-cover" /> : <i className="ri-image-line text-gray-300 text-3xl"></i>}
                      </div>
                      <div className="min-w-0">
                        <p className="text-base font-bold text-gray-900">{createForm.title || 'Titre de l’événement'}</p>
                        <p className="text-sm text-gray-500">{createForm.type || 'Catégorie'} · {createForm.diocese || 'Zone'}</p>
                        <p className="text-lg font-bold text-gold-dark mt-2">{ticketSummary(createForm.ticketTypes).ticketPrice > 0 ? `À partir de ${formatPrice(ticketSummary(createForm.ticketTypes).ticketPrice)}` : 'Gratuit'}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Types de tickets</p>
                      {ticketSummary(createForm.ticketTypes).ticketTypes.map((ticketType, index) => (
                        <div key={`${ticketType.name}-${index}`} className="flex items-center justify-between rounded-lg bg-white p-3">
                          <div>
                            <p className="text-sm font-bold text-gray-900">{ticketType.name}</p>
                            <p className="text-xs text-gray-400">{ticketType.seats == null ? 'Places non limitées' : `${ticketType.seats} places`}</p>
                          </div>
                          <p className="text-sm font-bold text-gold-dark">{formatPrice(ticketType.price)}</p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Début', value: createForm.date ? formatDate(createForm.date) : '—' },
                        { label: 'Fin', value: createForm.endDate ? formatDate(createForm.endDate) : '—' },
                        { label: 'Lieu', value: createForm.location || '—' },
                        { label: 'Places', value: String(ticketSummary(createForm.ticketTypes).totalSeats || '—') },
                        { label: 'Organisateur', value: createForm.organizerName || '—' },
                        { label: 'Contact', value: createForm.contact || '—' },
                      ].map((item) => (
                        <div key={item.label} className="bg-white rounded-lg p-3">
                          <p className="text-xs text-gray-400">{item.label}</p>
                          <p className="text-sm font-semibold text-gray-900">{item.value}</p>
                        </div>
                      ))}
                    </div>

                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Description</p>
                      <p className="text-sm text-gray-700">{createForm.description || '—'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 pb-6 pt-4 flex items-center gap-3 border-t border-gray-100">
              {createErrors.submit ? <p className="text-sm font-medium text-red-600">{createErrors.submit}</p> : null}
              {createStep > 1 && <button onClick={() => setCreateStep((s) => (s - 1) as Step)} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">← Retour</button>}
              <div className="flex-1"></div>
              {createStep < 3
                ? <button onClick={handleCreateNext} className="flex items-center gap-1.5 px-6 py-2.5 bg-gold text-white rounded-xl text-sm font-semibold hover:bg-gold-dark cursor-pointer whitespace-nowrap">Continuer →</button>
                : <button onClick={() => void saveCreate()} disabled={createSaving} className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gold to-gold-dark text-white rounded-xl text-sm font-semibold hover:from-gold-dark hover:to-[#a07828] cursor-pointer whitespace-nowrap disabled:opacity-60"><i className={createSaving ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>{createSaving ? 'Envoi...' : 'Soumettre pour validation'}</button>
              }
            </div>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">Modifier l'événement</h4>
              <button onClick={() => setEditTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={editForm.title} onChange={(e) => setEditForm((current) => ({ ...current, title: e.target.value }))} placeholder="Titre" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.type} onChange={(e) => setEditForm((current) => ({ ...current, type: e.target.value }))} placeholder="Catégorie" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input type="datetime-local" value={editForm.date.slice(0, 16)} onChange={(e) => setEditForm((current) => ({ ...current, date: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input type="datetime-local" value={editForm.endDate.slice(0, 16)} onChange={(e) => setEditForm((current) => ({ ...current, endDate: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.location} onChange={(e) => setEditForm((current) => ({ ...current, location: e.target.value }))} placeholder="Lieu" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.ticketPrice} onChange={(e) => setEditForm((current) => ({ ...current, ticketPrice: e.target.value }))} placeholder="Prix ticket" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <input value={editForm.totalSeats} onChange={(e) => setEditForm((current) => ({ ...current, totalSeats: e.target.value }))} placeholder="Places" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
            </div>
            <input value={editForm.contact} onChange={(e) => setEditForm((current) => ({ ...current, contact: e.target.value }))} placeholder="Contact" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 mt-3" />
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <img
                  src={editImagePreview || editForm.image}
                  alt={editForm.title || 'Événement'}
                  className="h-16 w-20 rounded-xl object-cover bg-gray-100"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-500">Image de l'événement</p>
                  <p className="truncate text-xs text-gray-400">{editImageFile ? editImageFile.name : 'Image actuelle ou URL'}</p>
                </div>
                <button type="button" onClick={() => editImageInputRef.current?.click()} className="rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white">
                  Changer
                </button>
              </div>
              <input ref={editImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleEditImageChange} />
              <input value={editImageFile ? '' : editForm.image} onChange={(e) => {
                setEditImageFile(null);
                setEditImagePreview(e.target.value);
                setEditForm((current) => ({ ...current, image: e.target.value }));
              }} placeholder="URL image" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 mt-3" />
            </div>
            <textarea value={editForm.description} onChange={(e) => setEditForm((current) => ({ ...current, description: e.target.value }))} placeholder="Description" className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-gold/40 mt-3" />
            {editError ? <p className="mt-3 text-sm font-medium text-red-600">{editError}</p> : null}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditTarget(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => void saveEdit()} disabled={editSaving} className="flex-1 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-light cursor-pointer whitespace-nowrap disabled:opacity-60">{editSaving ? 'Enregistrement...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDeleteTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-sm font-bold text-gray-900 mb-2">Supprimer cet événement ?</h4>
            <p className="text-sm text-gray-500 mb-4">L'événement <strong>{deleteTarget.title}</strong> sera déplacé dans la corbeille et ne sera plus visible dans l’application.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => void confirmDelete()} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
